import { StatsigClient, type StatsigOptions, type StatsigUser } from '@statsig/js-client'
import { StatsigSessionReplayPlugin } from '@statsig/session-replay'
import { StatsigAutoCapturePlugin } from '@statsig/web-analytics'
import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics } from './Analytics'

/**
 * 目前收費版本不支援客製化 environment 只能這樣轉換
 */
const getAppEnv = (appEnv: string) => {
  if (appEnv === 'beta') {
    return 'staging'
  }
  return appEnv
}

const defaultStatsigOptions: StatsigOptions = {
  plugins: [
    new StatsigSessionReplayPlugin(),
    new StatsigAutoCapturePlugin(),
  ],
}

export interface StatsigConfig {
  sdkKey?: string
  user: StatsigUser
  appEnv: string
  onStableIdReady?: (stableId: string) => void
}

export class Statsig extends DniAnalytics<AnalyticsEvents> {
  readonly config: StatsigConfig
  private static instance: Statsig | null = null
  private statsigClient: StatsigClient | null = null

  constructor(config: StatsigConfig) {
    super()
    this.config = config
    this.initialize()
  }

  // Static getInstance method for singleton pattern
  public static getInstance(config: StatsigConfig): Statsig {
    if (!Statsig.instance) {
      Statsig.instance = new Statsig(config)
    }
    return Statsig.instance
  }

  protected async initialize() {
    if (!this.config.sdkKey) {
      console.error('[Statsig] sdkKey is not defined')
      return
    }
    const statsigClient = new StatsigClient(this.config.sdkKey, this.config.user, {
      ...defaultStatsigOptions,
      environment: { tier: getAppEnv(this.config.appEnv) },
    })
    this.emitStableIdReady(statsigClient)
    try {
      await statsigClient.initializeAsync()
      this.statsigClient = statsigClient
      this.setIsReady(true)
    }
    catch (error) {
      console.error('[Statsig] initialize error', error)
    }
  }

  /**
   * stableID 由 SDK 從 cookie / localStorage 同步取得，不需等 initializeAsync，
   * 所以刻意在網路初始化前發出——否則初始化緩慢或失敗時，這段期間的 request 都不帶 header。
   */
  private emitStableIdReady(statsigClient: StatsigClient) {
    const { stableID } = statsigClient.getContext()
    if (!stableID) return
    try {
      this.config.onStableIdReady?.(stableID)
    }
    catch (error) {
      console.error('[Statsig] emitStableIdReady onStableIdReady', error)
    }
  }

  public getExperiment(experimentName: string) {
    if (!this.statsigClient) {
      console.error('[Statsig] statsigClient is not initialized')
      return null
    }
    return this.statsigClient.getExperiment(experimentName)
  }

  protected sendAnalyticsEvent(customEvent: BaseAnalyticsEvent) {
    if (!this.statsigClient) {
      console.error('[Statsig] statsigClient is not initialized')
      return
    }
    try {
      const { eventName, eventParams } = customEvent
      const value = 'value' in eventParams ? eventParams.value as string | number : undefined
      this.statsigClient.logEvent(
        eventName,
        value,
        eventParams as Record<string, string>,
      )
    }
    catch (error) {
      console.error('[Statsig] sendEvent', error)
    }
  }
}
